import { payloadFromScript } from '../DebitCardStep/DebitCardStepScriptParser';
export const DEFAULT_ERROR = 'There appears to be an issue with the debit card that was entered. Please re-enter your debit card information.';
/**
 * Converts user input expiration dates into expiration dates as required for payment gateway.
 *
 * @param input exp date as MM/YYYY
 * @returns exp date as YYYY-MM
 */
const formatExpiration = (input) => {
    const monthYear = input.split('/');
    return `${monthYear[1]}-${monthYear[0]}`;
};
/**
 * Returns the last 4 of a masked cc number, as returned from payment gateway.
 *
 * i.e. returns '1234' from an expected input of '******1234'
 */
// tslint:disable-next-line: no-magic-numbers
const maskedLast4 = (masked) => masked && masked.slice(-4);
/**
 * Combines script props and entered values into a well-formed HTTP fetch request to payment gateway.
 *
 * @param payload script props obtained from `parseScript`
 * @param inputs workflow ui input states from `IWorkflowUIState.inputState`
 * @returns a well-formed HTTP request to `fetch` against payment gateway
 */
const createRequest = (payload, inputs) => {
    if (!payload) {
        return null;
    }
    const TRUE_STRING_LITERAL = true.toString();
    return new Request(payload.paymentGatewayUrl, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Avant-Authorization': `Bearer ${payload.userToken}`
        },
        body: JSON.stringify({
            // tslint:disable: no-backbone-get-set-outside-model
            individual_name: inputs.get('cardholder_name'),
            card_number: inputs.get('debit_card_number'),
            expiration: formatExpiration(inputs.get('expiration_date') || ''),
            security_code: inputs.get('security_code'),
            zipcode: inputs.get('zip_code'),
            customer_id: payload.customerID,
            financial_owner: payload.financialOwner,
            state: payload.state,
            payment_date: payload.payment_date,
            payment_method_key: payload.payment_method_key,
            token_count: payload.token_count,
            account_reference: payload.account_reference,
            origin: payload.origin,
            recurring: payload.is_eligible_for_workflow_on_dash ? inputs.get('save_card_confirmation') === TRUE_STRING_LITERAL : false
            // tslint:enable: no-backbone-get-set-outside-model
        })
    });
};
export const parseResponse = (json) => {
    if ([
        // bail on obviously 'failed' responses or missing data...
        json.status === 'failed',
        !json.token,
        !json.credit_card_uuid,
        !maskedLast4(json.masked_number),
        // ...or if we have any server-provided errors
        !!json.errors
    ].some(condition => condition)) {
        // collect a printable set of server-provided errors (if available)
        const errors = (json.errors || []).filter(error => error.display_error)
            .map(error => error.display_error)
            .join('\n');
        return { error: errors || DEFAULT_ERROR };
    }
    // otherwise, assume we got a good, whole response
    return {
        token: json.token,
        uuid: json.credit_card_uuid,
        last4: maskedLast4(json.masked_number),
        brand: json.brand,
        cardType: json.type
    };
};
/**
 * Tokenizes a debit card using Payment Gateway
 *
 * @param step the current (`card_information`) step.
 * @param state the current local workflow ui state
 * @returns a promise on `ITokenizeDebitCardResponse` with the response from payment gateway
 */
export const getToken = async (step, inputs) => {
    const scriptPayload = payloadFromScript(step.script);
    const request = createRequest(scriptPayload, inputs);
    if (!scriptPayload || !request) {
        throw new Error(DEFAULT_ERROR);
    }
    try {
        const response = await fetch(request);
        // decode response -> object; malformed json should throw an error
        const json = await response.json();
        return parseResponse(json);
    }
    catch (e) {
        return { error: DEFAULT_ERROR };
    }
};
